import { Injectable } from '@angular/core';

export interface NpsValue {
  value: string;
  numericValue: number;
}

@Injectable({
  providedIn: 'root'
})
export class NpsValidationService {

  // Standard NPS values ordered from smallest to largest
  private readonly standardNpsValues: NpsValue[] = [
    { value: '1/8', numericValue: 0.125 },
    { value: '1/4', numericValue: 0.25 },
    { value: '3/8', numericValue: 0.375 },
    { value: '1/2', numericValue: 0.5 },
    { value: '3/4', numericValue: 0.75 },
    { value: '1', numericValue: 1 },
    { value: '1-1/4', numericValue: 1.25 },
    { value: '1-1/2', numericValue: 1.5 },
    { value: '2', numericValue: 2 },
    { value: '2-1/2', numericValue: 2.5 },
    { value: '3', numericValue: 3 },
    { value: '3-1/2', numericValue: 3.5 },
    { value: '4', numericValue: 4 },
    { value: '5', numericValue: 5 },
    { value: '6', numericValue: 6 },
    { value: '8', numericValue: 8 },
    { value: '10', numericValue: 10 },
    { value: '12', numericValue: 12 },
    { value: '14', numericValue: 14 },
    { value: '16', numericValue: 16 },
    { value: '18', numericValue: 18 },
    { value: '20', numericValue: 20 },
    { value: '24', numericValue: 24 },
    { value: '26', numericValue: 26 },
    { value: '28', numericValue: 28 },
    { value: '30', numericValue: 30 },
    { value: '32', numericValue: 32 },
    { value: '34', numericValue: 34 },
    { value: '36', numericValue: 36 },
    { value: '42', numericValue: 42 },
    { value: '48', numericValue: 48 }
  ];

  /**
   * Validates if the given NPS value is free text without letters (except 'x')
   * Allows: numbers, symbols, spaces but blocks letters
   */
  isValidNps(value: string): boolean {
    if (!value || value.trim() === '') {
      return true; // Allow empty values
    }

    const trimmedValue = value.trim();

    // Check if it contains only non-letter characters (except 'x' for multiplication)
    return this.hasNoInvalidLetters(trimmedValue);
  }

  /**
   * Checks if value contains invalid letters (allows 'x' but blocks other letters)
   */
  private hasNoInvalidLetters(value: string): boolean {
    // Block all letters except 'x' (case insensitive)
    const hasInvalidLetters = /[a-wyzA-WYZ]/;
    return !hasInvalidLetters.test(value);
  }

  /**
   * Validates NPS combination/range patterns with more flexible rules
   */
  private isValidNpsCombination(value: string): boolean {
    // Pattern 1: Simple range like "1/2 - 2" or "3/4-1-1/2"
    const simpleRangePattern = /^(.+?)\s*-\s*(.+)$/;

    // Pattern 2: Complex range like "(2 - 3) x (3/4 - 1-1/2)"
    const complexRangePattern = /^\((.+?)\s*-\s*(.+?)\)\s*x\s*\((.+?)\s*-\s*(.+?)\)$/i;

    // Pattern 3: Multiplication like "2 x 1" or "1/2x3/4"
    const multiplicationPattern = /^(.+?)\s*x\s*(.+)$/i;

    // Pattern 4: Fractional measurements like "1/2", "3/4", "1-1/2"
    //const fractionalPattern = /^(\d+)(-\d+)?\/\d+$|^\d+(-\d+\/\d+)?$/;

    // Check complex range pattern first
    const complexMatch = value.match(complexRangePattern);
    if (complexMatch) {
      const [, start1, end1, start2, end2] = complexMatch;
      return this.isValidMeasurement(start1) &&
             this.isValidMeasurement(end1) &&
             this.isValidMeasurement(start2) &&
             this.isValidMeasurement(end2);
    }

    // Check simple range pattern
    const simpleMatch = value.match(simpleRangePattern);
    if (simpleMatch) {
      const [, start, end] = simpleMatch;
      return this.isValidMeasurement(start) && this.isValidMeasurement(end);
    }

    // Check multiplication pattern
    const multMatch = value.match(multiplicationPattern);
    if (multMatch) {
      const [, nps1, nps2] = multMatch;
      return this.isValidMeasurement(nps1) && this.isValidMeasurement(nps2);
    }

    // Check if it's a single valid measurement (even if not in standard list)
    return this.isValidMeasurement(value);
  }

  /**
   * Validates a single measurement (more flexible than standard NPS list)
   */
  private isValidMeasurement(value: string): boolean {
    const trimmed = value.trim();

    // Check if it's in our standard list
    if (this.standardNpsValues.some(nps => nps.value === trimmed)) {
      return true;
    }

    // Check common measurement patterns:
    // - Simple numbers: "2", "10", "24"
    // - Simple fractions: "1/2", "3/4", "1/8"
    // - Mixed fractions: "1-1/2", "2-1/4", "3-3/4"
    const measurementPatterns = [
      /^\d+$/,                    // Simple numbers: 2, 10, 24
      /^\d+\/\d+$/,              // Simple fractions: 1/2, 3/4
      /^\d+-\d+\/\d+$/,          // Mixed fractions: 1-1/2, 2-1/4
      /^\d+\.\d+$/,              // Decimal numbers: 2.5, 10.75
    ];

    return measurementPatterns.some(pattern => pattern.test(trimmed));
  }

  /**
   * Checks if a string contains only letters (to reject pure text)
   */
  private isOnlyLetters(value: string): boolean {
    return /^[a-zA-Z\s]+$/.test(value.trim());
  }

  /**
   * Gets the numeric value for a given NPS string for sorting purposes
   * For ranges, returns the smallest value for sorting
   */
  getNpsNumericValue(value: string): number {
    if (!value) return 0;

    const trimmed = value.trim();

    // Check if it's a simple standard NPS value
    const npsValue = this.standardNpsValues.find(nps => nps.value === trimmed);
    if (npsValue) {
      return npsValue.numericValue;
    }

    // For ranges, extract the smallest NPS value for sorting
    return this.getSmallestNpsFromRange(trimmed);
  }

  /**
   * Extracts the smallest NPS value from a range for sorting
   */
  private getSmallestNpsFromRange(value: string): number {
    // Pattern 1: Complex range like "(2 - 3) x (3/4 - 1-1/2)"
    const complexRangePattern = /^\((.+?)\s*-\s*(.+?)\)\s*x\s*\((.+?)\s*-\s*(.+?)\)$/;

    // Pattern 2: Simple range like "1/2 - 2"
    const simpleRangePattern = /^(.+?)\s*-\s*(.+)$/;

    // Pattern 3: Multiplication like "2 x 1"
    const multiplicationPattern = /^(.+?)\s*x\s*(.+)$/i;

    const values: number[] = [];

    // Check complex range pattern
    const complexMatch = value.match(complexRangePattern);
    if (complexMatch) {
      const [, start1, end1, start2, end2] = complexMatch;
      values.push(
        this.getSingleNpsNumericValue(start1),
        this.getSingleNpsNumericValue(end1),
        this.getSingleNpsNumericValue(start2),
        this.getSingleNpsNumericValue(end2)
      );
    } else {
      // Check simple range pattern
      const simpleMatch = value.match(simpleRangePattern);
      if (simpleMatch) {
        const [, start, end] = simpleMatch;
        values.push(
          this.getSingleNpsNumericValue(start),
          this.getSingleNpsNumericValue(end)
        );
      } else {
        // Check multiplication pattern
        const multMatch = value.match(multiplicationPattern);
        if (multMatch) {
          const [, nps1, nps2] = multMatch;
          values.push(
            this.getSingleNpsNumericValue(nps1),
            this.getSingleNpsNumericValue(nps2)
          );
        }
      }
    }

    // Return the smallest value, or 0 if no valid values found
    return values.length > 0 ? Math.min(...values.filter(v => v > 0)) : 0;
  }

  /**
   * Gets numeric value for a single NPS (helper method)
   */
  private getSingleNpsNumericValue(value: string): number {
    const trimmed = value.trim();
    const npsValue = this.standardNpsValues.find(nps => nps.value === trimmed);
    return npsValue ? npsValue.numericValue : 0;
  }

  /**
   * Sorts an array of items by NPS value (smallest to largest)
   */
  sortByNps<T>(items: T[], getNpsValue: (item: T) => string): T[] {
    return items.sort((a, b) => {
      const npsA = this.getNpsNumericValue(getNpsValue(a));
      const npsB = this.getNpsNumericValue(getNpsValue(b));
      return npsA - npsB;
    });
  }

  /**
   * Gets all standard NPS values
   */
  getStandardNpsValues(): string[] {
    return this.standardNpsValues.map(nps => nps.value);
  }

  /**
   * Parses an NPS string into its from/to boundary components.
   *
   * Format 1 — Scalar:         "2"           → { npsFrom: '2',   npsTo: '2'   }
   * Format 2 — Simple range:   "1/2 - 1-1/2" → { npsFrom: '1/2', npsTo: '1-1/2' }
   *                             "14 - 24"     → { npsFrom: '14',  npsTo: '24'  }
   *   Separator is always ' - ' (space-dash-space). Individual values may contain
   *   un-spaced dashes as part of a mixed fraction (e.g. 1-1/2 = 1½") — those are
   *   not separators.
   * Format 3 — Single reducer:  "2 x 3/4"     → null (free-text until SME defines UX)
   * Format 4 — Compound reducer: "(6-24) x (2-16)" → null (free-text until SME defines UX)
   *
   * Returns null for reducers (nps contains ' x ') — npsFrom/npsTo left untouched.
   */
  parseNpsRange(nps: string): { npsFrom: string; npsTo: string } | null {
    if (!nps || nps.trim() === '') return null;
    const trimmed: string = nps.trim();

    // Formats 3 & 4: ' x ' (space-x-space) marks reducers — leave npsFrom/npsTo as-is
    if (trimmed.includes(' x ')) return null;

    // Format 2: ' - ' (space-dash-space) is the range separator
    // This safely ignores un-spaced dashes inside mixed fractions (e.g. '1-1/2')
    const separatorIndex: number = trimmed.indexOf(' - ');
    if (separatorIndex !== -1) {
      return {
        npsFrom: trimmed.substring(0, separatorIndex).trim(),
        npsTo: trimmed.substring(separatorIndex + 3).trim(),
      };
    }

    // Format 1: scalar — npsFrom and npsTo are the same value
    return { npsFrom: trimmed, npsTo: trimmed };
  }

  /**
   * Gets validation error message
   */
  getValidationErrorMessage(): string {
    return 'No se permiten letras en el campo NPS (excepto "x" para multiplicación)';
  }

  /**
   * Formats NPS value suggestions for autocomplete
   */
  getSuggestedNpsValues(input: string): string[] {
    if (!input) return this.getStandardNpsValues().slice(0, 10);

    const inputLower = input.toLowerCase().trim();

    // First, try to match exact NPS values
    const exactMatches = this.standardNpsValues
      .filter(nps => nps.value.toLowerCase().includes(inputLower))
      .map(nps => nps.value);

    // Then, add some common range patterns if user is typing ranges
    const rangePatterns: string[] = [];
    if (inputLower.includes('x') || inputLower.includes('-') || inputLower.includes('(')) {
      rangePatterns.push(
        '1/2 - 2',
        '3/4 - 1-1/2',
        '2 x 1',
        '(2 - 3) x (3/4 - 1)',
        '(1 - 2) x (1/2 - 3/4)'
      );
    }

    return [...exactMatches, ...rangePatterns].slice(0, 10);
  }
}
